import React, { useState, useEffect, useRef } from 'react';
import { C, card, sectionTitle, filterPill, kpiCard, kpiIcon } from '../../styles';
import { getCustomerInsights, listCustomers, getCustomerProfile, getVisualPrefs, setVisualPref, getCustomerBirthdayMonths, getCustomerBirthdayDays, getCustomerFilterOptions } from '../../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── interactive chart legend helpers ─────────────────────────────────────────
const useChartLegend = (items) => {
  const [selected, setSelected] = React.useState(() => new Set(items));
  const key = items.join('||');
  React.useEffect(() => { setSelected(new Set(items)); }, [key]); // eslint-disable-line
  const toggle = (ref, name) => {
    ref?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendToggleSelect', name });
    setSelected(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  };
  const selectAll = (ref) => {
    ref?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendAllSelect' });
    setSelected(new Set(items));
  };
  const resetAll = (ref) => {
    const c = ref?.current?.getEchartsInstance?.();
    if (c) { c.dispatchAction({ type: 'legendAllSelect' }); c.dispatchAction({ type: 'legendInverseSelect' }); }
    setSelected(new Set());
  };
  return { selected, toggle, selectAll, resetAll };
};

const ChartLegend = ({ items, colors, chartRef, selected, onToggle, onSelectAll, onReset }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 10px', padding:'6px 0 0' }}>
    <button onClick={() => onSelectAll(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.accent}`, borderRadius:'4px', color:C.accent, cursor:'pointer', flexShrink:0 }}>All</button>
    <button onClick={() => onReset(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer', flexShrink:0 }}>Reset</button>
    {items.map((name, i) => {
      const isSel = selected.has(name);
      return (
        <div key={name} onClick={() => onToggle(chartRef, name)}
          style={{ display:'flex', alignItems:'center', gap:'3px', cursor:'pointer', opacity: isSel ? 1 : 0.3, flexShrink:0 }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors[i % colors.length] }}/>
          <span style={{ fontSize:'9px', color: C.textSec }}>{name}</span>
        </div>
      );
    })}
  </div>
);

// ─── MultiSelect ──────────────────────────────────────────────────────────────
const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen]     = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val];
    onChange(next);
  };

  const filtered = (options || []).filter(opt => {
    const lbl = typeof opt === 'object' ? (opt.label || opt.name || '') : String(opt || '');
    return String(lbl).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div ref={ref} style={{ position:'relative', flex:1, minWidth:'130px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', padding:'4px 10px', background:C.bgSurface, border:`1px solid ${selected.length ? C.accent : C.border}`, borderRadius:'8px', fontSize:'11px', color:selected.length ? C.accent : C.textMuted, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
      >
        <span>{label}{selected.length ? ` (${selected.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:300, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'6px 8px', borderBottom:`1px solid ${C.border}` }}>
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." onClick={e => e.stopPropagation()}
              style={{ width:'100%', padding:'5px 8px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'6px', fontSize:'11px', color:C.textPrimary, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ maxHeight:'150px', overflowY:'auto' }}>
            {filtered.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:C.textMuted }}>No results</p>}
            {filtered.map((opt, i) => {
              const val = typeof opt === 'object' ? (opt.key || opt.code || String(opt)) : String(opt || '');
              const lbl = typeof opt === 'object' ? (opt.label || opt.name || val) : String(opt || '');
              const isSel = selected.includes(val);
              return (
                <div key={i} onClick={() => toggle(val)} style={{ padding:'7px 10px', fontSize:'11px', color:isSel ? C.accent : C.textSec, background:isSel ? C.accentDim : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background:isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                  </span>
                  {lbl}
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}` }}>
              <button onClick={() => { onChange([]); setOpen(false); }}
                style={{ fontSize:'10px', color:C.danger, background:'none', border:'none', cursor:'pointer' }}>Clear All</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  if (typeof isoDate === 'string' && isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-GB');
};

const QUICK = [
  { id: 'yesterday',       label: 'Yesterday'        },
  { id: 'this_year',       label: 'This Year'         },
  { id: 'this_month',      label: 'This Month'        },
  { id: 'this_week',       label: 'This Week'         },
  { id: 'last_year',       label: 'Last Year'         },
  { id: 'last_month',      label: 'Last Month'        },
  { id: 'last_week',       label: 'Last Week'         },
  { id: 'last_year_month', label: "Last Year's Month" },
  { id: 'custom',          label: 'Custom'            },
  { id: 'all',             label: 'All Time'          },
];

const KPICard = ({ title, value, icon, iconBg, sub }) => (
  <div style={kpiCard}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
      <div style={kpiIcon(iconBg)}>{icon}</div>
    </div>
    <p style={{ fontSize:'11px', color:C.textSec, margin:'0 0 4px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</p>
    <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0, lineHeight:1.2 }}>{value}</p>
    {sub && <p style={{ fontSize:'10px', color:C.textMuted, margin:'3px 0 0' }}>{sub}</p>}
  </div>
);

const CollapsibleSection = ({ visKey, title, collapsed, onToggle, children }) => (
  <div style={{ ...card, marginBottom: '12px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: collapsed ? 0 : '8px' }}>
      <p style={{ ...sectionTitle, marginBottom:0 }}>{title}</p>
      <button onClick={() => onToggle(visKey)}
        style={{ fontSize:'10px', color:C.textSec, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'3px 8px', cursor:'pointer' }}>
        {collapsed ? 'Expand' : 'Collapse'}
      </button>
    </div>
    {!collapsed && children}
  </div>
);

const CustomerSelect = ({ customers, selected, onSelect, loading, onSearch }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchChange = (e) => { setSearch(e.target.value); onSearch(e.target.value); };

  const filtered = customers.filter(c =>
    c.member_name.toLowerCase().includes(search.toLowerCase()) ||
    c.member_code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} style={{ position:'relative', flex:1 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width:'100%', padding:'10px 12px', background:C.bgSurface, border:`1px solid ${selected ? C.accent : C.border}`, borderRadius:'8px', fontSize:'13px', color: selected ? C.accent : C.textSec, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>{selected ? selected.member_name : 'Select customer...'}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'6px 8px', borderBottom:`1px solid ${C.border}` }}>
            <input autoFocus value={search} onChange={handleSearchChange} placeholder="Search by name or code..."
              style={{ width:'100%', padding:'8px 10px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'6px', fontSize:'12px', color:C.textPrimary, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ maxHeight:'200px', overflowY:'auto' }}>
            {loading && <div style={{ padding:'10px', textAlign:'center', color:C.textMuted }}>Loading...</div>}
            {!loading && filtered.length === 0 && <div style={{ padding:'10px', textAlign:'center', color:C.textMuted }}>No customers found</div>}
            {filtered.map(c => (
              <div key={c.member_code}
                onClick={() => { onSelect(c); setOpen(false); setSearch(''); }}
                style={{ padding:'8px 12px', fontSize:'12px', color:C.textSec, cursor:'pointer', borderBottom:`1px solid ${C.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.bgElevated}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontWeight:'500', color:C.textPrimary }}>{c.member_name}</span>
                <span style={{ marginLeft:'8px', color:C.textMuted, fontSize:'10px' }}>({c.member_code})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const CustomerInsights = () => {
  const [insights, setInsights]                   = useState(null);
  const [loading, setLoading]                     = useState(true);
  const [activePeriod, setAP]                     = useState(() => localStorage.getItem('ccs_period') || 'this_month');
  const setPeriod = (p) => { localStorage.setItem('ccs_period', p); setAP(p); };

  // Pie chart refs for interactive legends
  const ageRef    = useRef(null);
  const genderRef = useRef(null);

  // Bar click-to-highlight states
  const [selSpend,    setSelSpend]    = useState(null);
  const [selVisit,    setSelVisit]    = useState(null);
  const [selClassify, setSelClassify] = useState(null);
  const [selAge,      setSelAge]      = useState(null);
  // ── Branch multi-select (boss + area_manager only, persists in localStorage) ─
  const [branches,        setBranches]    = useState([]);
  const [selectedBranches, setSelected]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [pendingBranches, setPending] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [showBranchDrop, setShowBranchDrop] = useState(false);
  const openBranchDrop = () => { if (showBranchDrop) { setShowBranchDrop(false); } else { setPending(selectedBranches); setShowBranchDrop(true); } };
  const toggleBranch   = (code) => setPending(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const applyBranches  = () => { localStorage.setItem('ccs_branch', JSON.stringify(pendingBranches)); setSelected(pendingBranches); setShowBranchDrop(false); };
  const resetBranches  = () => { setPending([]); setSelected([]); localStorage.setItem('ccs_branch', '[]'); setShowBranchDrop(false); };

  // Load available branches for this tenant
  useEffect(() => {
    import('../../services/api').then(({ default: api }) => {
      api.get('/api/branches').then(r => setBranches(r.data.branches || [])).catch(() => {});
    });
  }, []);

    // Compute date range synchronously from period string
  const _computeDates = (period) => {
    const now = new Date();
    const _p  = n => String(n).padStart(2, '0');
    const _f  = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    if (period === 'yesterday')      { const y = new Date(now); y.setDate(y.getDate()-1); return [_f(y), _f(y)]; }
    if (period === 'this_month')     return [_f(new Date(now.getFullYear(), now.getMonth(), 1)), _f(now)];
    if (period === 'this_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day+1); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'this_year')      return [_f(new Date(now.getFullYear(), 0, 1)), _f(now)];
    if (period === 'last_year')      return [_f(new Date(now.getFullYear()-1, 0, 1)), _f(new Date(now.getFullYear()-1, 11, 31))];
    if (period === 'last_month')     return [_f(new Date(now.getFullYear(), now.getMonth()-1, 1)), _f(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (period === 'last_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day-6); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'last_year_month'){ const m = now.getMonth(), ly = now.getFullYear()-1; return [_f(new Date(ly,m,1)), _f(new Date(ly,m+1,0))]; }
    if (period === 'custom')        return [localStorage.getItem('ccs_from')||'', localStorage.getItem('ccs_to')||''];
    return ['', ''];
  };
  const _initPeriod = localStorage.getItem('ccs_period') || 'this_month';
  const [_initFrom, _initTo] = _computeDates(_initPeriod);
  const _initPendFrom = _initPeriod === 'custom' ? (localStorage.getItem('ccs_from')||'') : '';
  const _initPendTo   = _initPeriod === 'custom' ? (localStorage.getItem('ccs_to')||'')   : '';
const [dateFrom, setDateFrom]                   = useState(_initFrom);
  const [dateTo, setDateTo]                       = useState(_initTo);
  const [pendingFrom, setPendingFrom]             = useState(_initPendFrom);
  const [pendingTo, setPendingTo]                 = useState(_initPendTo);
  const [customers, setCustomers]                 = useState([]);
  const [loadingCustomers, setLoadingCust]        = useState(false);
  const [selectedCustomer, setSelectedCustomer]   = useState(null);
  const [profile, setProfile]                     = useState(null);
  const [loadingProfile, setLoadingProf]          = useState(false);
  const [collapsed, setCollapsed]                 = useState({});
  const [filterCustomers,  setFilterCustomers] = useState([]); // options list from API
  const [activeMembers,    setActiveMembers]   = useState([]); // applied member_codes
  const [pendingMembers,   setPendingMembers]  = useState([]); // in-flight selections
  const [filtersOpen,      setFO]              = useState(false);
  const [filterSearch,     setFilterSearch]    = useState('');
  const [birthdayMonths, setBirthdayMonths]       = useState([]);
  const [noDob,          setNoDob]               = useState(0);

  // ── Birthday drill-down state ─────────────────────────────────────────────
  const [birthdayDrillMonth, setBDMonth] = useState(null); // null = month view, 1-12 = day view
  const [birthdayDays,       setBDDays]  = useState([]);
  const [loadingBDays,       setLBDays]  = useState(false);

  useEffect(() => {
    getVisualPrefs().then(r => setCollapsed(r.data || {})).catch(console.error);
  }, []);

  const toggleCollapse = (key) => {
    const newVal = !collapsed[key];
    setCollapsed(prev => ({ ...prev, [key]: newVal }));
    setVisualPref({ visual_key: key, is_collapsed: newVal }).catch(console.error);
  };

  // Quick filter → dates
  useEffect(() => {
    if (activePeriod === 'custom') return;
    const now = new Date();
    const _p = n => String(n).padStart(2, '0');
    const _f = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    let from = '', to = '';
    if (activePeriod === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      from = to = _f(y);
    } else if (activePeriod === 'this_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth(), 1));
      to   = _f(now);
    } else if (activePeriod === 'this_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'this_year') {
      from = _f(new Date(now.getFullYear(), 0, 1));
      to   = _f(now);
    } else if (activePeriod === 'last_year') {
      from = _f(new Date(now.getFullYear() - 1, 0, 1));
      to   = _f(new Date(now.getFullYear() - 1, 11, 31));
    } else if (activePeriod === 'last_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to   = _f(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (activePeriod === 'last_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day - 6); // last Monday
      const e = new Date(d); e.setDate(d.getDate() + 6); // last Sunday
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'last_year_month') {
      const m = now.getMonth();
      const ly = now.getFullYear() - 1;
      from = _f(new Date(ly, m, 1));
      to   = _f(new Date(ly, m + 1, 0));
    }
    // 'all' → from/to stay '' → no date filter sent to API
    setPendingFrom(from);
    setPendingTo(to);
    setDateFrom(from);
    setDateTo(to);
  }, [activePeriod]);

  useEffect(() => { loadData(); loadCustomers(''); }, [dateFrom, dateTo, selectedBranches, activeMembers]);

  // Re-fetch profile when date filter changes if a customer is already selected
  useEffect(() => {
    if (!selectedCustomer) return;
    setLoadingProf(true);
    getCustomerProfile(selectedCustomer.member_code, params())
      .then(res => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoadingProf(false));
  }, [dateFrom, dateTo, selectedBranches, activeMembers]);

  const params = () => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    if (activeMembers.length)        p.member_code = activeMembers.join(',');
    return p;
  };

  // Load customer filter options whenever date/branch changes
  useEffect(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    if (filterSearch) p.q = filterSearch;
    getCustomerFilterOptions(p).then(r => setFilterCustomers(r.data?.customers || [])).catch(() => {});
  }, [dateFrom, dateTo, selectedBranches, filterSearch]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getCustomerInsights(params());
      setInsights(res.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const p = params();
    getCustomerBirthdayMonths(p)
      .then(res => {
        const d = res.data || {};
        // New format: { months: [...], no_dob: N }
        if (d.months) {
          setBirthdayMonths(d.months);
          setNoDob(d.no_dob || 0);
        } else {
          // Old format: flat array (backward compat)
          const flat = Array.isArray(d) ? d : [];
          setBirthdayMonths(flat.filter(r => r.month !== 0));
          setNoDob((flat.find(r => r.month === 0) || {}).count || 0);
        }
      })
      .catch(() => {});
  }, [dateFrom, dateTo, selectedBranches, activeMembers]);

  // ── Birthday drill-down handler ───────────────────────────────────────────
  const handleBirthdayMonthClick = async (monthIndex) => {
    const month = monthIndex + 1; // 0-indexed → 1-12
    setBDMonth(month);
    setLBDays(true);
    try {
      const res = await getCustomerBirthdayDays(month, params());
      setBDDays(res.data || []);
    } catch(e) { console.error(e); }
    finally { setLBDays(false); }
  };

  const loadCustomers = async (searchTerm) => {
    setLoadingCust(true);
    try { const res = await listCustomers({ q: searchTerm }); setCustomers(res.data); }
    catch(e) { console.error(e); }
    finally { setLoadingCust(false); }
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingProf(true);
    try { const res = await getCustomerProfile(customer.member_code, params()); setProfile(res.data); }
    catch(e) { console.error(e); setProfile(null); }
    finally { setLoadingProf(false); }
  };

  const applyCustom = () => {
    localStorage.setItem('ccs_from', pendingFrom);
    localStorage.setItem('ccs_to', pendingTo);
    setDateFrom(pendingFrom);
    setDateTo(pendingTo);
  };

  // Chart data
  const topSpenders = insights?.top_spenders || [];
  const topVisitors = insights?.top_visitors  || [];
  const byClassify  = insights?.by_classify   || [];
  const byAge       = insights?.by_age        || [];

  const chartBase = {
    backgroundColor: 'transparent',
    tooltip: { trigger:'axis', backgroundColor: C.bgElevated, borderColor: C.border, textStyle: { color:C.textPrimary, fontSize:11 } },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
  };
  const xAxisBase = (data) => ({
    type:'category', data,
    axisLine:{ lineStyle:{ color:C.border } }, axisTick:{ show:false },
    axisLabel:{ color:C.textMuted, fontSize:9, rotate:30, interval:0, overflow:'truncate', width:70 },
  });
  const yAxisRM    = { type:'value', splitLine:{ lineStyle:{ color:C.bgElevated } }, axisLabel:{ color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v } };
  const yAxisCount = { type:'value', splitLine:{ lineStyle:{ color:C.bgElevated } }, axisLabel:{ color:C.textMuted, fontSize:10 } };

  const topSpendersChart = { ...chartBase, xAxis: xAxisBase(topSpenders.map(s => (s.member_name || s.member_code).substring(0, 14))), yAxis: yAxisRM, series:[{ type:'bar', data: topSpenders.map((s,i)=>({ value:parseFloat(s.revenue||0), itemStyle:{ color:C.accent, opacity: selSpend === null ? 1-i*0.07 : (selSpend === i ? 1 : 0.2), borderRadius:[4,4,0,0] } })) }] };
  const topVisitorsChart  = { ...chartBase, xAxis: xAxisBase(topVisitors.map(s => (s.member_name || s.member_code).substring(0, 14))), yAxis: yAxisCount, series:[{ type:'bar', data: topVisitors.map((s,i)=>({ value:parseInt(s.visits||0), itemStyle:{ color:C.blue, opacity: selVisit === null ? 1-i*0.07 : (selVisit === i ? 1 : 0.2), borderRadius:[4,4,0,0] } })) }] };
  const classifyChart     = { ...chartBase, xAxis: xAxisBase(byClassify.slice(0,10).map(c => (c.classify||'Unknown').substring(0,16))), yAxis: yAxisRM, series:[{ type:'bar', data: byClassify.slice(0,10).map((c,i)=>({ value:parseFloat(c.revenue||0), itemStyle:{ color:C.purple, opacity: selClassify === null ? 1-i*0.07 : (selClassify === i ? 1 : 0.2), borderRadius:[4,4,0,0] } })) }] };
  const ageChart          = { ...chartBase, xAxis: xAxisBase(byAge.slice(0,10).map(a => a.age_group||'Unknown')), yAxis: yAxisRM, series:[{ type:'bar', data: byAge.slice(0,10).map((a,i)=>({ value:parseFloat(a.revenue||0), itemStyle:{ color:C.amber, opacity: selAge === null ? 1-i*0.07 : (selAge === i ? 1 : 0.2), borderRadius:[4,4,0,0] } })) }] };

  // Interactive pie legend items
  const CI_COLORS = [C.accent, C.blue, C.purple, C.amber, '#FF6B6B', '#34D399', '#F472B6', '#38BDF8'];
  const ageItems    = (insights?.by_age    ||[]).filter(r => parseInt(r.transactions||0) > 0).map(r => String(r.age_group||'Unknown'));
  const genderItems = (insights?.by_gender ||[]).filter(r => parseInt(r.customers||0)   > 0).map(r => r.gender||'Unknown');
  const GCOL = { 'Female':'#F472B6', 'Male':'#38BDF8', 'Unknown':C.textMuted };
  const ageColors    = ageItems.map((_,i) => CI_COLORS[i%CI_COLORS.length]);
  const genderColors = genderItems.map(r => GCOL[r] || C.textMuted);
  // eslint-disable-next-line
  const agePieLegend    = useChartLegend(ageItems);
  // eslint-disable-next-line
  const genderPieLegend = useChartLegend(genderItems);

  // Birthday months chart (12 bars only — no_data shown as stat below)
  const BIRTH_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const birthdayCounts = Array(12).fill(0);
  birthdayMonths.forEach(r => {
    if (r.month >= 1 && r.month <= 12) birthdayCounts[r.month - 1] = r.count;
  });
  const birthdayChart = {
    ...chartBase,
    tooltip: {
      trigger: 'axis',
      backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => `${BIRTH_MONTHS[params[0].dataIndex]}: ${params[0].value.toLocaleString()} members (tap to drill down)`
    },
    xAxis: {
      type: 'category', data: BIRTH_MONTHS,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color: C.textMuted, fontSize: 9, interval: 0 }
    },
    yAxis: yAxisCount,
    series: [{
      type: 'bar',
      data: birthdayCounts.map((v) => ({
        value: v,
        itemStyle: { color: C.purple, borderRadius: [4,4,0,0] },
        cursor: 'pointer',
      }))
    }]
  };

  // Birthday day drill-down chart
  const birthdayDayChart = (() => {
    const dayCounts = Array(31).fill(0);
    birthdayDays.forEach(r => { if (r.day >= 1 && r.day <= 31) dayCounts[r.day - 1] = r.count; });
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: C.bgElevated, borderColor: C.border,
        textStyle: { color: C.textPrimary, fontSize: 11 },
        formatter: (params) => `Day ${params[0].dataIndex + 1}: ${params[0].value.toLocaleString()} members`
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 31 }, (_, i) => String(i + 1)),
        axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
        axisLabel: { color: C.textMuted, fontSize: 9, interval: 1 }
      },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: C.bgElevated } }, axisLabel: { color: C.textMuted, fontSize: 10 } },
      grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: true },
      series: [{ type: 'bar', data: dayCounts.map(v => ({ value: v, itemStyle: { color: C.purple, borderRadius: [4,4,0,0] } })) }]
    };
  })();

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', border:`3px solid ${C.bgElevated}`, borderTopColor:C.purple, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <p style={{ color:C.textMuted, fontSize:'13px' }}>Loading customer data...</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding:'16px', paddingBottom:'32px' }}>

      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        {QUICK.map(q => <button key={q.id} onClick={() => setPeriod(q.id)} style={filterPill(activePeriod === q.id)}>{q.label}</button>)}
      </div>
          {/* Branch multi-select — boss, area_manager, superadmin only */}
          {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              if (!['boss','area_manager','superadmin'].includes(u.role)) return null;
            } catch { return null; }
            if (branches.length === 0) return null;
            const label = selectedBranches.length === 0 ? 'All Branches'
              : selectedBranches.length === 1 ? (branches.find(b => b.code === selectedBranches[0])?.name || selectedBranches[0])
              : `${selectedBranches.length} Branches`;
            return (
              <div style={{ marginBottom:'10px', position:'relative', display:'inline-block' }}>
                <button onClick={openBranchDrop}
                  style={{ ...filterPill(selectedBranches.length > 0), display:'flex', alignItems:'center', gap:'6px', padding:'6px 14px' }}>
                  🏢 {label} <span style={{ fontSize:'10px', opacity:0.7 }}>{showBranchDrop ? '▴' : '▾'}</span>
                </button>
                {showBranchDrop && (
                  <div style={{ position:'absolute', top:'38px', left:0, zIndex:999,
                    background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'12px',
                    padding:'8px', minWidth:'180px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
                    {branches.map(b => (
                      <label key={b.code} style={{ display:'flex', alignItems:'center', gap:'8px',
                        padding:'7px 10px', cursor:'pointer', borderRadius:'8px', fontSize:'13px',
                        color: pendingBranches.includes(b.code) ? C.accent : C.textSec,
                        background: pendingBranches.includes(b.code) ? 'rgba(168,224,99,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={pendingBranches.includes(b.code)}
                          onChange={() => toggleBranch(b.code)} style={{ accentColor: C.accent }} />
                        {b.name || b.code}
                      </label>
                    ))}
                    <div style={{ borderTop:`1px solid ${C.border}`, marginTop:'6px', paddingTop:'6px', display:'flex', flexDirection:'column', gap:'4px' }}>
                      <button onClick={applyBranches}
                        style={{ width:'100%', background:C.accent, border:'none', borderRadius:'6px',
                          color:'#000', fontSize:'12px', fontWeight:'700', cursor:'pointer', padding:'6px 0' }}>
                        Apply
                      </button>
                      <button onClick={resetBranches}
                        style={{ width:'100%', background:'transparent', border:'none',
                          color:C.textMuted, fontSize:'12px', cursor:'pointer', padding:'4px 0' }}>
                        ↩ Reset to All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      {activePeriod === 'custom' && (
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'flex-end' }}>
          {[{label:'From', val:pendingFrom, set:setPendingFrom}, {label:'To', val:pendingTo, set:setPendingTo}].map(({label, val, set}) => (
            <div key={label} style={{ flex:1 }}>
              <p style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>{label}</p>
              <input type="date" value={val} onChange={e => set(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'12px', color: val ? C.textPrimary : C.textMuted, outline:'none', boxSizing:'border-box' }}
              />
            </div>
          ))}
          <button onClick={applyCustom} disabled={!pendingFrom || !pendingTo}
            style={{ padding:'8px 14px', background: pendingFrom && pendingTo ? C.accent : C.bgElevated, color: pendingFrom && pendingTo ? '#000' : C.textMuted, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor: pendingFrom && pendingTo ? 'pointer' : 'default', flexShrink:0 }}>
            Apply
          </button>
        </div>
      )}

      {/* ── CUSTOMER FILTER ── */}
      <div style={{ marginBottom:'12px' }}>
        <button onClick={() => setFO(o => !o)}
          style={{ ...filterPill(activeMembers.length > 0), display:'flex', alignItems:'center', gap:'6px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Customer{activeMembers.length > 0 ? ` (${activeMembers.length})` : ''}
          <span style={{ fontSize:'9px' }}>{filtersOpen ? '▲' : '▼'}</span>
        </button>

        {filtersOpen && (
          <div style={{ marginTop:'8px', padding:'12px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px' }}>
            <input
              value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
              placeholder="Search customer name or code…"
              style={{ width:'100%', padding:'8px 10px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'12px', color:C.textPrimary, outline:'none', boxSizing:'border-box', marginBottom:'8px' }}
            />
            <div style={{ maxHeight:'180px', overflowY:'auto', border:`1px solid ${C.border}`, borderRadius:'8px' }}>
              {filterCustomers.length === 0
                ? <p style={{ padding:'10px', textAlign:'center', fontSize:'11px', color:C.textMuted }}>No results</p>
                : filterCustomers.map(c => {
                    const isSel = pendingMembers.includes(c.code);
                    return (
                      <div key={c.code} onClick={() => setPendingMembers(prev => isSel ? prev.filter(x => x !== c.code) : [...prev, c.code])}
                        style={{ padding:'7px 10px', fontSize:'11px', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px',
                          background: isSel ? C.accentDim : 'transparent', color: isSel ? C.accent : C.textSec, borderBottom:`1px solid ${C.border}` }}>
                        <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background:isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                        </span>
                        <span style={{ fontWeight: isSel ? '600' : '400' }}>{c.name}</span>
                        <span style={{ fontSize:'10px', color:C.textMuted, marginLeft:'auto' }}>{c.code}</span>
                      </div>
                    );
                  })
              }
            </div>
            <div style={{ display:'flex', gap:'8px', marginTop:'10px', justifyContent:'flex-end' }}>
              <button onClick={() => { setPendingMembers([]); setActiveMembers([]); setFO(false); }}
                style={{ fontSize:'11px', color:C.textMuted, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'5px 12px', cursor:'pointer' }}>
                Clear
              </button>
              <button onClick={() => { setActiveMembers(pendingMembers); setFO(false); }}
                style={{ padding:'5px 20px', background:C.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      <CollapsibleSection visKey="ci_kpi" title="Summary" collapsed={!!collapsed['ci_kpi']} onToggle={toggleCollapse}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          <KPICard title="Visit Customers"    value={(insights?.unique_customers||0).toLocaleString()}                    iconBg={C.accentDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z" stroke={C.accent} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Spending Customers"  value={(insights?.spending_customers||0).toLocaleString()}                                                                    iconBg={C.blueDim}   icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke={C.blue} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="New Customers"      value={(insights?.new_customers||0).toLocaleString()}                       iconBg={C.amberDim}  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8zM20 8v6M23 11h-6" stroke={C.amber} strokeWidth="2" strokeLinecap="round"/></svg>} sub="In selected period" />
          <KPICard title="Total Customers"    value={(insights?.total_customers||0).toLocaleString()}                     iconBg={C.purpleDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke={C.purple} strokeWidth="2" strokeLinecap="round"/></svg>} sub="Active members (lifetime)" />
        </div>
      </CollapsibleSection>

      {/* ── CUSTOMERS BY AGE ── */}
      {(insights?.by_age||[]).length > 0 && (
        <CollapsibleSection visKey="ci_by_age" title="Customers by Age Group" collapsed={!!collapsed['ci_by_age']} onToggle={toggleCollapse}>
          <ReactECharts ref={ageRef} option={(() => {
            const data = (insights.by_age||[]).filter(r => parseInt(r.transactions||0) > 0);
            return {
              backgroundColor: 'transparent',
              tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{color:C.textPrimary, fontSize:11},
                formatter: p => `${p.name}<br/>${p.value} customers (${p.percent}%)` },
              legend: { show: false, data: data.map(r => String(r.age_group||'Unknown')) },
              series:[{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
                data: data.map((r,i) => ({ name: String(r.age_group||'Unknown'), value: parseInt(r.transactions||0), itemStyle:{color:CI_COLORS[i%CI_COLORS.length]} })),
                label:{show:false} }],
            };
          })()} style={{ height:'180px', width:'100%' }} />
          <ChartLegend items={ageItems} colors={ageColors} chartRef={ageRef}
            selected={agePieLegend.selected} onToggle={agePieLegend.toggle}
            onSelectAll={agePieLegend.selectAll} onReset={agePieLegend.resetAll} />
        </CollapsibleSection>
      )}

      {/* ── CUSTOMERS BY GENDER ── */}
      {(insights?.by_gender||[]).length > 0 && (
        <CollapsibleSection visKey="ci_gender" title="Customers by Gender" collapsed={!!collapsed['ci_gender']} onToggle={toggleCollapse}>
          <ReactECharts ref={genderRef} option={(() => {
            const data = insights.by_gender.filter(r => parseInt(r.customers||0) > 0);
            return {
              backgroundColor: 'transparent',
              tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{color:C.textPrimary,fontSize:11},
                formatter: p => `${p.name}<br/>${p.value} customers (${p.percent}%)` },
              legend: { show: false, data: data.map(r => r.gender||'Unknown') },
              series:[{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
                data: data.map(r => ({ name: r.gender||'Unknown', value: parseInt(r.customers||0), itemStyle:{color:GCOL[r.gender]||C.textMuted} })),
                label:{show:false} }],
            };
          })()} style={{ height:'180px', width:'100%' }} />
          <ChartLegend items={genderItems} colors={genderColors} chartRef={genderRef}
            selected={genderPieLegend.selected} onToggle={genderPieLegend.toggle}
            onSelectAll={genderPieLegend.selectAll} onReset={genderPieLegend.resetAll} />
        </CollapsibleSection>
      )}

      <CollapsibleSection visKey="ci_search" title="Customer Search & Profile" collapsed={!!collapsed['ci_search']} onToggle={toggleCollapse}>
        <CustomerSelect customers={customers} selected={selectedCustomer} onSelect={handleSelectCustomer} loading={loadingCustomers} onSearch={loadCustomers} />
        {loadingProfile && <p style={{ textAlign:'center', color:C.textMuted, padding:'12px', fontSize:'13px' }}>Loading profile...</p>}
        {profile && !loadingProfile && (
          <div style={{ marginTop:'12px', border:`1px solid ${C.border}`, borderRadius:'10px', overflow:'hidden' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'12px 14px', background:C.bgElevated }}>
              <div>
                <p style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, margin:0 }}>{profile.member?.member_name}</p>
                <p style={{ fontSize:'11px', color:C.textMuted, margin:'3px 0 0' }}>{profile.member?.member_code} · {profile.member?.phone_mobile || 'No phone'}</p>
              </div>
              <button onClick={() => { setProfile(null); setSelectedCustomer(null); }}
                style={{ background:C.bgSurface, border:`1px solid ${C.border}`, color:C.textSec, borderRadius:'50%', width:'26px', height:'26px', cursor:'pointer', fontSize:'12px' }}>✕</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', borderBottom:`1px solid ${C.border}` }}>
              {[
                { label:'Total Spent',  value:`RM ${parseFloat(profile.stats?.total_spent||0).toLocaleString(undefined,{maximumFractionDigits:0})}` },
                { label:'Visits',       value:(profile.stats?.total_visits||0).toString() },
                { label:'Last Visit',   value: profile.stats?.last_visit ? formatDate(profile.stats.last_visit) : 'N/A' },
                { label:'Outstanding',  value:`RM ${parseFloat(profile.stats?.total_pending||0).toLocaleString(undefined,{maximumFractionDigits:0})}` },
              ].map((s, i) => (
                <div key={i} style={{ padding:'10px', textAlign:'center', borderRight: i < 3 ? `1px solid ${C.border}` : 'none', background:C.bgSurface }}>
                  <p style={{ fontSize:'10px', color:C.textMuted, textTransform:'uppercase', margin:'0 0 4px' }}>{s.label}</p>
                  <p style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, margin:0 }}>{s.value}</p>
                </div>
              ))}
            </div>
            <p style={{ fontSize:'12px', fontWeight:'600', color:C.textSec, padding:'10px 14px 4px', margin:0 }}>Transaction History</p>
            <div style={{ maxHeight:'260px', overflowY:'auto', overflowX:'auto' }}>
              <table style={{ width:'max-content', minWidth:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                <thead>
                  <tr style={{ background:C.bgElevated }}>
                    <th style={{ padding:'8px 10px', textAlign:'left', color:C.textMuted, whiteSpace:'nowrap' }}>Date</th>
                    <th style={{ padding:'8px 10px', textAlign:'left', color:C.textMuted, whiteSpace:'nowrap', minWidth:'140px' }}>Product</th>
                    <th style={{ padding:'8px 10px', textAlign:'right', color:C.textMuted, whiteSpace:'nowrap' }}>Paid (RM)</th>
                    <th style={{ padding:'8px 10px', textAlign:'right', color:C.textMuted, whiteSpace:'nowrap' }}>Pending (RM)</th>
                    <th style={{ padding:'8px 10px', textAlign:'left', color:C.textMuted, whiteSpace:'nowrap' }}>Served By</th>
                  </tr>
                </thead>
                <tbody>
                  {(profile.transactions || []).map((t, i) => (
                    <tr key={i} style={{ borderTop:`1px solid ${C.bgElevated}` }}>
                      <td style={{ padding:'8px 10px', color:C.textSec, whiteSpace:'nowrap' }}>{formatDate(t.sale_date)}</td>
                      <td style={{ padding:'8px 10px', color:C.textPrimary, fontSize:'11px', minWidth:'140px', maxWidth:'200px', wordBreak:'break-word' }}>{t.product_name}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', color:C.accent, fontWeight:'500', whiteSpace:'nowrap' }}>{parseFloat(t.payment_amount||0).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', color:C.danger, fontWeight:'500', whiteSpace:'nowrap' }}>{Math.abs(parseFloat(t.pending||0)).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                      <td style={{ padding:'8px 10px', color:C.textSec, whiteSpace:'nowrap', fontSize:'11px' }}>{t.server_name || t.sold_code || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection visKey="ci_spenders" title="Top 10 Customers by Revenue" collapsed={!!collapsed['ci_spenders']} onToggle={toggleCollapse}>
        {topSpenders.length > 0 ? (
          <>
            <ReactECharts option={topSpendersChart} style={{ height:'220px' }}
              onEvents={{ click: (p) => setSelSpend(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
            {selSpend !== null && <button onClick={() => setSelSpend(null)} style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>Clear highlight</button>}
          </>
        ) : <div style={{ textAlign:'center', padding:'40px 0', color:C.textMuted, fontSize:'13px' }}>No revenue data for selected period</div>}
      </CollapsibleSection>

      <CollapsibleSection visKey="ci_visitors" title="Top 10 Customers by Visits" collapsed={!!collapsed['ci_visitors']} onToggle={toggleCollapse}>
        {topVisitors.length > 0 ? (
          <>
            <ReactECharts option={topVisitorsChart} style={{ height:'220px' }}
              onEvents={{ click: (p) => setSelVisit(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
            {selVisit !== null && <button onClick={() => setSelVisit(null)} style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>Clear highlight</button>}
          </>
        ) : <div style={{ textAlign:'center', padding:'40px 0', color:C.textMuted, fontSize:'13px' }}>No visit data for selected period</div>}
      </CollapsibleSection>

      <CollapsibleSection visKey="ci_classify" title="Revenue by Classify" collapsed={!!collapsed['ci_classify']} onToggle={toggleCollapse}>
        {byClassify.length > 0 ? (
          <>
            <ReactECharts option={classifyChart} style={{ height:'220px' }}
              onEvents={{ click: (p) => setSelClassify(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
            {selClassify !== null && <button onClick={() => setSelClassify(null)} style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>Clear highlight</button>}
          </>
        ) : <div style={{ textAlign:'center', padding:'40px 0', color:C.textMuted, fontSize:'13px' }}>No classify data for selected period</div>}
      </CollapsibleSection>

      <CollapsibleSection visKey="ci_age" title="Revenue by Age Group" collapsed={!!collapsed['ci_age']} onToggle={toggleCollapse}>
        {byAge.length > 0 ? (
          <>
            <ReactECharts option={ageChart} style={{ height:'220px' }}
              onEvents={{ click: (p) => setSelAge(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
            {selAge !== null && <button onClick={() => setSelAge(null)} style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>Clear highlight</button>}
          </>
        ) : <div style={{ textAlign:'center', padding:'40px 0', color:C.textMuted, fontSize:'13px' }}>No age group data for selected period</div>}
      </CollapsibleSection>

      {/* ── BIRTHDAY CHART with drill-down ── */}
      <CollapsibleSection visKey="ci_birthday" title="Members by Birth Month" collapsed={!!collapsed['ci_birthday']} onToggle={toggleCollapse}>
        {birthdayDrillMonth === null ? (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
              <p style={{ fontSize:'11px', color:C.textMuted, margin:0 }}>
                Tap a bar to see day-by-day breakdown.
              </p>
              {noDob > 0 && (
                <span style={{ fontSize:'11px', color:C.textMuted, padding:'2px 8px', background:C.bgElevated, borderRadius:'6px' }}>
                  No DOB on record: <b style={{ color:C.textSec }}>{noDob.toLocaleString()}</b>
                </span>
              )}
            </div>
            <ReactECharts
              option={birthdayChart}
              style={{ height:'240px', width:'100%' }}
              onEvents={{ click: (p) => handleBirthdayMonthClick(p.dataIndex) }}
            />
          </>
        ) : (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
              <button
                onClick={() => { setBDMonth(null); setBDDays([]); }}
                style={{ fontSize:'11px', color:C.purple, background:'none', border:`1px solid ${C.purple}`, borderRadius:'6px', padding:'3px 10px', cursor:'pointer' }}>
                ← Back
              </button>
              <p style={{ fontSize:'12px', color:C.textPrimary, margin:0, fontWeight:'600' }}>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][birthdayDrillMonth - 1]} — Birthdays by Day
              </p>
            </div>
            {loadingBDays ? (
              <div style={{ textAlign:'center', padding:'40px 0', color:C.textMuted, fontSize:'13px' }}>
                <div style={{ width:'28px', height:'28px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.purple, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 8px' }}/>
                Loading...
              </div>
            ) : (
              <ReactECharts option={birthdayDayChart} style={{ height:'240px' }} />
            )}
          </>
        )}
      </CollapsibleSection>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CustomerInsights;